export type CalendarViewMode = 'day' | 'week' | 'month' | 'year';

const CALENDAR_STATE_KEY = 'aircs-react-calendar-v1';
const CALENDAR_VIEWS = new Set<CalendarViewMode>(['day', 'week', 'month', 'year']);

function calendarStateKey(year: number): string {
  return `${CALENDAR_STATE_KEY}:${year}`;
}

// Only the view MODE is cached now. The anchor date is no longer persisted:
// the calendar always opens on the current period (see CalendarPage T15).
function sanitizedCalendarState(raw: unknown): { view?: CalendarViewMode } {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const candidate = raw as { view?: unknown };
  return typeof candidate.view === 'string' && CALENDAR_VIEWS.has(candidate.view as CalendarViewMode)
    ? { view: candidate.view as CalendarViewMode }
    : {};
}

export function readCalendarState(year: number): { view?: CalendarViewMode } {
  if (typeof window === 'undefined') return {};
  try {
    return sanitizedCalendarState(JSON.parse(window.localStorage.getItem(calendarStateKey(year)) || '{}'));
  } catch {
    return {};
  }
}

export function writeCalendarState(year: number, state: { view: CalendarViewMode }): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(calendarStateKey(year), JSON.stringify(sanitizedCalendarState(state)));
  } catch {
    // Browser storage can be disabled. Calendar navigation still works in memory.
  }
}

/** Remove person/resource identifiers (and any legacy anchor date) persisted by older calendar versions. */
export function purgeLegacyCalendarResourceIds(
  storage: Storage | undefined = typeof window === 'undefined' ? undefined : window.localStorage,
): number {
  if (!storage) return 0;
  let purged = 0;
  try {
    const keys = Array.from({ length: storage.length }, (_, index) => storage.key(index)).filter(Boolean) as string[];
    for (const key of keys) {
      if (!key.startsWith(`${CALENDAR_STATE_KEY}:`)) continue;
      try {
        const raw = JSON.parse(storage.getItem(key) || '{}') as Record<string, unknown>;
        if (!raw || typeof raw !== 'object' || Array.isArray(raw) || !Object.prototype.hasOwnProperty.call(raw, 'resourceId')) {
          continue;
        }
        storage.setItem(key, JSON.stringify(sanitizedCalendarState(raw)));
        purged += 1;
      } catch {
        // Do not delete an unreadable preference as part of a targeted migration.
      }
    }
  } catch {
    // Browser storage can be unavailable; this cleanup is best effort.
  }
  return purged;
}

// ── Pure guard helpers for the store monthKey → local anchor synchronisation ──
// Extracted so the anti-loop logic can be unit tested in isolation (T01).

/** Parse the exercise year out of a `YYYY-MM` month key. Returns null when malformed. */
export function monthKeyYear(monthKey: string | null | undefined): number | null {
  if (typeof monthKey !== 'string') return null;
  const match = /^(\d{4})-(\d{2})$/.exec(monthKey);
  if (!match) return null;
  const month = Number(match[2]);
  if (month < 1 || month > 12) return null;
  return Number(match[1]);
}

/** True when `monthKey` is a valid month of the given exercise year. */
export function monthKeyMatchesYear(monthKey: string | null | undefined, year: number): boolean {
  return monthKeyYear(monthKey) === year;
}

export interface MonthKeyGuardInput {
  /** Month key coming from the shared store (external navigation intent). */
  monthKey: string | null | undefined;
  /** `anchorDate.slice(0, 7)` — the month the calendar currently shows. */
  anchorMonthKey: string;
  /** Currently selected exercise. */
  selectedYear: number;
  /** Last month key this page already applied from the store (ref guard). */
  lastAppliedMonthKey: string | null;
}

/**
 * Decide whether a monthKey coming from the shared store should move the local
 * calendar anchor. It applies only when it belongs to the selected exercise,
 * points to a different month than the anchor, and has not already been applied.
 * These three guards together kill the anchor <-> store echo that previously
 * looped forever (React error #185) when switching exercise.
 */
export function shouldApplyExternalMonthKey({
  monthKey,
  anchorMonthKey,
  selectedYear,
  lastAppliedMonthKey,
}: MonthKeyGuardInput): boolean {
  if (monthKeyYear(monthKey) !== selectedYear) return false;
  if (monthKey === anchorMonthKey) return false;
  if (monthKey === lastAppliedMonthKey) return false;
  return true;
}

/** Reset the exercise-scoped facets in one store update. */
export function calendarExerciseResetFilters(monthKey: string): { monthKey: string; resourceId: 'all' } {
  return { monthKey, resourceId: 'all' };
}
